import axios from 'axios';
import pick from 'lodash/pick'

export class ApiError extends Error {
  constructor(message, data = null, status = null) {
    super(message);
    this.name = 'ApiError';
    this.data = data;
    this.status = status;
  }
}

axios.defaults.baseURL = process.env.VUE_APP_API_BASE_URL || "http://localhost:3000";
axios.defaults.auth = {username: 'hambone', password: 'fakenamington'};

// goal here is to hide all complexity of fetching data / setting object defaults / error handling, etc.
// expose easy to use Promise based api
export default {
  createCustomer(customerEmail) {
    return new Promise((resolve, reject) => {
      axios({
        url: '/v1/customers.json',
        method: 'post',
        data: { email: customerEmail },
        validateStatus: (status) => {
          return status === 201 // new record when 201, existing when 400
        }
      }).then(response => {
        // defaults
        if (!response.data.record.country) { response.data.record.country = 'United States' }
        resolve(response.data.record);
      }).catch(error => {
        if (error.response.status === 400 && error.response.data.errors.base[0] === 'Validation failed: Email has already been taken, Shopify has already been taken') {
          // customer already exists with given email
          reject(new ApiError('Email already taken', {email: ['already taken'], customer: error.response.data.record}, 400))
        } else if (error.response.status === 422) {
          reject(new ApiError('Validation failed', error.response.data.errors, 422))
        }
      })
    });
  },
  getCustomer(customerId) {
    return new Promise((resolve, reject) => {
      axios.get(`/v1/customers/${customerId}.json`).then(response => {
        // defaults
        if (!response.data.record.country) { response.data.record.country = 'United States' }
        resolve(response.data.record);
      }).catch(error => {
        reject(error);
      });
    });
  },
  getCustomers() {
    return new Promise((resolve, reject) => {
      axios.get(`/v1/customers.json`).then(response => {
        resolve(response.data.records);
      }).catch(error => {
        reject(error);
      });
    });
  },
  getCustomerWithVerificationToken(verificationToken) {
    return new Promise((resolve, reject) => {
      axios.get(`/v1/customer_verifications/${verificationToken}.json`).then(response => {
        // defaults
        if (!response.data.record.country) { response.data.record.country = 'United States' }
        resolve(response.data.record);
      }).catch(error => {
        reject(error);
      });
    })
  },
  getCustomerInstagram(customerId) {
    return new Promise((resolve, reject) => {
      axios.get(`/v1/customers/${customerId}/instagram.json`).then(response => {
        resolve(response.data.record);
      }).catch(error => {
        reject(new ApiError(error.message, null, error.response?.status));
      });
    });
  },
  updateCustomer(customerId, attrs) {
    const allAttributes =  [
      'first_name', 'last_name', 'instagram_handle', 'phone', 'address1', 'address2', 'city', 'zip', 'province', 'country', 'tier',
      'tops', 'bottoms', 'fitted_hats', 'footwear', 'accessories', 'notification_preference', 'customer_types', 'customer_sub_types', 'default', 'name', 'avatar', 'remove_avatar',
      'email', 'address_nickname', 'default', 'nickname', 'tops_sizes_confirmed', 'bottoms_sizes_confirmed', 'footwear_sizes_confirmed', 'headwear_sizes_confirmed', 'accessories_sizes_confirmed'
    ];

    if (attrs.avatar) {
      let formData = new FormData()
      console.log(allAttributes)
      Object.entries(attrs).forEach(
        ([key, value]) => formData.append(key, value)
      )
      return new Promise((resolve, reject) => {
        axios.patch(
          `/v1/customers/${customerId}.json`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        ).then(response => {
          resolve(response.data.record);
        }).catch(error => {
          reject(new ApiError('Update failed', error.response.data.errors, error.response?.status))
        })
      })
    } else {
      return new Promise((resolve, reject) => {
        axios.patch(`/v1/customers/${customerId}.json`,
          pick(attrs, allAttributes)
        ).then(response => {
          resolve(response.data.record);
        }).catch(error => {
          reject(new ApiError('Update failed', error.response.data.errors, error.response?.status))
        })
      })
    }
  },
  requestCustomerVerification(customerId) {
    return axios.post(`/v1/customers/${customerId}/verification.json`)
  },
  verifyCustomer(verificationToken, attrs) {
    const allAttributes =  [
      'first_name', 'last_name', 'instagram_handle', 'phone', 'address1', 'address2', 'city', 'zip', 'province', 'country',
      'tops', 'bottoms', 'fitted_hats', 'footwear', 'accessories', 'notification_preference'
    ]; // except: tier

    return new Promise((resolve, reject) => {
      axios.patch(`/v1/customer_verifications/${verificationToken}.json`, {
        record: pick(attrs, allAttributes)
      }).then(response => {
        resolve(response.data.record);
      }).catch(error => {
        reject(new ApiError('Verification failed', error.response.data.errors, error.response?.status))
      })
    })
  },
  sendEmail(params) {
    return axios.post(`/v1/emails.json`, { message: params });
  },
  sendText(params) {
    return axios.post(`/v1/sms.json`, { message: params });
  },
  getCustomerNotes(customerId) {
    return new Promise((resolve, reject) => {
      axios.get(`/v1/customers/${customerId}/customer_notes.json`).then(response => {
        resolve(response.data.records);
      }).catch(error => {
        reject(new ApiError(error.message, null, error.response?.status));
      });
    });
  },
  createCustomerType(params) {
    return axios.post('/v1/types', { record: params });
  },
  getOrderCategories() {
    return axios.get('/v1/categories.json')
  },
  getCustomerTypes() {
    return axios.get('/v1/types.json')
  },
  getCustomerSubTypes() {
    return axios.get('/v1/sub_types');
  },
  createCustomerSubType(params) {
    return axios.post(`/v1/types/${params.type_id}/sub_types`, { record: params });
  },
  updateCustomerSubType(params) {
    return axios.put(`/v1/types/${params.type_id}/sub_types/${params.id}`, { record: params });
  },
  deleteCustomerSubType(params) {
    return axios.delete(`/v1/types/${params.type_id}/sub_types/${params.id}`);
  },
  updateCustomerType(params) {
    return axios.put(`/v1/types/${params.id}`, { record: params });
  },
  deleteCustomerType(params) {
    return axios.delete(`/v1/types/${params.id}`);
  },
  getTypeColors() {
    return axios.get('/v1/type_colors/');
  },
  getCustomerOrders(customerId, query) {
    return new Promise((resolve, reject) => {
      axios.get(`/v1/customers/${customerId}/customer_orders.json${query || ''}`).then(response => {
        resolve(response.data.records);
      }).catch(error => {
        reject(new ApiError(error.message, null, error.response?.status));
      });
    });
  },
  updateOrder(orderId, params) {
    return axios.patch(`/v1/orders/${orderId}`, { record: params })
  },
  cancelOrder(orderId) {
    return axios.post(`/v1/orders/${orderId}/cancel.json`)
  },
  createCustomerNote(params) {
    return axios.post(`/v1/customers/${params.customer_id}/customer_notes.json`, { record: params });
  },
  updateCustomerNote(params) {
    return axios.patch(`/v1/customers/${params.customer_id}/customer_notes/${params.id}.json`, { record: params });
  },
  deleteCustomerNote(params) {
    return axios.delete(`/v1/customers/${params.customer_id}/customer_notes/${params.id}.json`);
  },
  deleteCustomerAddress(shopifyId, id){
    return axios.delete(`/v1/customer_addresses/${shopifyId}.json`, {data:{customer_id: id}});
  },
  updateCustomerAddress(params, id) {
    return axios.patch(`/v1/customer_addresses/${params.shopify_id}.json`, { record: params, customer_id: id });
  },
  createCustomerAddress(params, id) {
    return axios.post(`/v1/customer_addresses.json`, { record: params, customer_id: id });
  },
}
